/**
 * Events are what flows wait for: a message arrives, a job finishes, a form is submitted, a call
 * comes in. Each kind is posted to its own endpoint under `/events`, matched against the triggers
 * of that kind, and marked processed once the runs it started are prepared.
 *
 * Every kind carries the same handful of fields — who it belongs to, when it was processed — and
 * adds whatever a trigger of that kind can filter on.
 */
import { OneOrManySchema } from 'notable-common';
import { z } from 'zod';
import { FlowFormEventType, FlowJobTerminalStatus } from '../flowTrigger/eventTriggers.js';
import { FlowRpaJobKind } from '../rpaJob/rpaJob.js';
export var FlowEventKind;
(function (FlowEventKind) {
    FlowEventKind["HL7Message"] = "HL7_MESSAGE";
    FlowEventKind["X12Message"] = "X12_MESSAGE";
    FlowEventKind["SftpMessage"] = "SFTP_MESSAGE";
    FlowEventKind["VoiceCall"] = "VOICE_CALL";
    FlowEventKind["JobCompletion"] = "JOB_COMPLETION";
    FlowEventKind["Form"] = "FORM";
})(FlowEventKind || (FlowEventKind = {}));
/** Where an event is between arriving and having started its runs. */
export var FlowEventState;
(function (FlowEventState) {
    FlowEventState["Pending"] = "Pending";
    FlowEventState["Claimed"] = "Claimed";
    FlowEventState["Processed"] = "Processed";
    FlowEventState["Failed"] = "Failed";
})(FlowEventState || (FlowEventState = {}));
/** Which moment of a call the event reports. */
export var FlowVoiceEventType;
(function (FlowVoiceEventType) {
    FlowVoiceEventType["Start"] = "Start";
    FlowVoiceEventType["End"] = "End";
})(FlowVoiceEventType || (FlowVoiceEventType = {}));
/** Path parameters the endpoints addressing one event accept, whatever its kind. */
export const FlowEventIdParamsSchema = z.object({
    id: z.uuid().describe('The event.'),
});
/**
 * What every event is posted with, whatever kind it is. The state and the row timestamps are the
 * service's to set, so they are not here; `processed` is, because a caller replaying history may
 * post an event that is already dealt with.
 */
const EventCreateFieldsSchema = z.object({
    organizationId: z.uuid().describe('Organization the event belongs to.'),
    practiceId: z
        .uuid()
        .nullable()
        .describe('Practice it belongs to, where there is one.')
        .optional(),
    processed: z.coerce
        .date()
        .nullable()
        .describe('When the event was dealt with, if it already has been.')
        .optional(),
});
/**
 * The queue's own columns, which four of the six endpoints have always taken on the way in. The
 * x12 and sftp bodies were written by hand and never did, so they still do not.
 */
const EventQueueFieldsSchema = z.object({
    priority: z.number().nullable().describe('Where the event sits in the queue.').optional(),
    claimedBy: z.string().nullable().describe('Worker that has taken the event.').optional(),
    claimedAt: z.coerce.date().nullable().describe('When it was taken.').optional(),
    lastProcessingFailure: z
        .record(z.string(), z.unknown())
        .nullable()
        .describe('Why the last attempt failed.')
        .optional(),
});
/** What every event list may be narrowed by, whatever kind it is. */
const EventListFieldsSchema = z.object({
    id: OneOrManySchema(z.uuid()).describe('Return only these events.').optional(),
    organizationId: OneOrManySchema(z.uuid())
        .describe('Return only events of this organization, or of any of these organizations.')
        .optional(),
    practiceId: z.uuid().nullable().describe('Return only events of this practice.').optional(),
    processed: z.coerce
        .date()
        .nullable()
        .describe('Return only events dealt with at this moment.')
        .optional(),
});
/** Body `POST /events/hl7-messages` accepts. */
export const FlowHl7MessageEventCreateBodySchema = EventCreateFieldsSchema.extend(EventQueueFieldsSchema.shape).extend({
    messageType: z.string(),
    messageTrigger: z.string(),
    sendingFacility: z.string(),
    messageControlId: z.string(),
    patientEhrId: z.string(),
    message: z.record(z.string(), z.any()).describe('The parsed message.'),
    customMapping: z.record(z.string(), z.any()).nullable().optional(),
});
export const FlowHl7MessageEventUpdateBodySchema = FlowHl7MessageEventCreateBodySchema.partial();
export const FlowHl7MessageEventListQuerySchema = EventListFieldsSchema.extend({
    messageControlId: z.string().optional(),
    messageType: z.string().optional(),
    messageTrigger: z.string().optional(),
    sendingFacility: z.string().optional(),
    patientEhrId: z.string().optional(),
});
/** Body `POST /events/x12-messages` accepts. */
export const FlowX12MessageEventCreateBodySchema = EventCreateFieldsSchema.extend({
    interchangeSenderId: z.string(),
    transactionSetIdCode: z.string(),
    transactionSetPurposeCode: z.string(),
    interchangeControlNumber: z.string(),
    message: z.record(z.string(), z.unknown()).describe('The parsed message.'),
    customMapping: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const FlowX12MessageEventUpdateBodySchema = FlowX12MessageEventCreateBodySchema.partial();
export const FlowX12MessageEventListQuerySchema = EventListFieldsSchema.extend({
    kind: z.enum(FlowEventKind).optional(),
    interchangeSenderId: z.string().optional(),
    transactionSetIdCode: z.string().optional(),
    transactionSetPurposeCode: z.string().optional(),
    interchangeControlNumber: z.string().optional(),
});
/** Body `POST /events/sftp-messages` accepts. */
export const FlowSftpMessageEventCreateBodySchema = EventCreateFieldsSchema.extend({
    fileType: z.string(),
    message: z.record(z.string(), z.unknown()).describe('The parsed file.'),
    customMapping: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const FlowSftpMessageEventUpdateBodySchema = FlowSftpMessageEventCreateBodySchema.partial();
export const FlowSftpMessageEventListQuerySchema = EventListFieldsSchema.extend({
    fileType: z.string().optional(),
});
/** Body `POST /events/voice-calls` accepts. */
export const FlowVoiceEventCreateBodySchema = EventCreateFieldsSchema.extend(EventQueueFieldsSchema.shape).extend({
    voiceCallId: z.uuid().describe('Voice call that produced the event.').optional(),
    type: z.enum(FlowVoiceEventType).describe('Whether the call started or ended.'),
    phoneNumber: z.string(),
    metadata: z.record(z.string(), z.unknown()).describe('Whatever the telephony provider sent.'),
});
export const FlowVoiceEventUpdateBodySchema = FlowVoiceEventCreateBodySchema.partial();
export const FlowVoiceEventListQuerySchema = EventListFieldsSchema.extend({
    voiceCallId: z.uuid().optional(),
    type: z.enum(FlowVoiceEventType).optional(),
    phoneNumber: z.string().optional(),
});
/** Body `POST /events/job-completion` accepts. */
export const FlowJobCompletionEventCreateBodySchema = EventCreateFieldsSchema.extend(EventQueueFieldsSchema.shape).extend({
    rpaJobId: z.uuid().describe('The job that finished.'),
    jobKind: z.enum(FlowRpaJobKind),
    terminalStatus: z.enum(FlowJobTerminalStatus).describe('How it finished.'),
});
export const FlowJobCompletionEventUpdateBodySchema = FlowJobCompletionEventCreateBodySchema.partial();
export const FlowJobCompletionEventListQuerySchema = EventListFieldsSchema.extend({
    rpaJobId: z.uuid().optional(),
    jobKind: z.enum(FlowRpaJobKind).optional(),
    terminalStatus: z.enum(FlowJobTerminalStatus).optional(),
});
/** Body `POST /events/forms` accepts. */
export const FlowFormEventCreateBodySchema = EventCreateFieldsSchema.extend(EventQueueFieldsSchema.shape).extend({
    type: z.enum(FlowFormEventType).describe('Which moment in the form this reports.'),
    previsitFormId: z.uuid(),
    previsitFormTemplateId: z.uuid(),
    previsitFormSetId: z.uuid(),
});
/**
 * Body `PATCH /events/forms` accepts, which is the one field the endpoint has ever taken. The
 * handler authorizes the organization the event is already in, so a body that could carry another
 * one would move the event out from under that check.
 */
export const FlowFormEventUpdateBodySchema = z.object({
    processed: z.coerce.date().nullable().describe('When the event was dealt with.').optional(),
});
export const FlowFormEventListQuerySchema = EventListFieldsSchema.extend({
    type: OneOrManySchema(z.enum(FlowFormEventType)).optional(),
    previsitFormId: z.uuid().optional(),
    previsitFormTemplateId: z.uuid().optional(),
    previsitFormSetId: z.uuid().optional(),
});
//# sourceMappingURL=event.js.map